import Input from 'antd/es/input/Input'
import IdeyaTypography from '../../../../../common/antd/ideya-typography.tsx'
import { convertPxToNumber } from '../../../../../utils/helpers.ts'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'
import { FormInstance, FormProps } from 'antd/lib'
import { IdeyaButton } from '../../../../../common/antd/ideya-button/ideya-button.tsx'
import { IdeyaDatePicker } from '../../../../../common/antd/ideya-datepicker.tsx'
import {
  useCreateManagedTickerMutation,
  useUpdateManagedTickerMutation,
} from '../../../../../apis/ticker-management/ticker-management-api.api.ts'
import { useCallback, useEffect } from 'react'
import { getErrorDescription } from '../../../../../redux/utils.ts'
import notification from 'antd/es/notification/index'
import message from 'antd/es/message/index'
import Form from 'antd/es/form/index'
import Select from 'antd/es/select/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Radio from 'antd/es/radio/index'
import dayjs from 'dayjs'
import Switch from 'antd/es/switch/index'
import { useTranslation } from 'react-i18next'
import { regionOptions } from '../../../../../utils/selector-options.ts'

export type TickerManagementFormFieldTypes = {
  bbgCompositeKey: string
  region: string
  canLong: boolean
  canShort: boolean
  startDate: string
  endDate: string
  isAdr: boolean
}

interface TickerManagementFormProps {
  onCancel: () => void
  form: FormInstance<TickerManagementFormFieldTypes>
  editTickerId?: string
  mode: 'create' | 'edit'
}

export const TickerManagementForm: React.FC<TickerManagementFormProps> = ({
  onCancel,
  form,
  editTickerId,
  mode,
}) => {
  const { t } = useTranslation()
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [createManagedTicker, { isLoading, isSuccess, isError, error, reset }] =
    useCreateManagedTickerMutation()

  const region = Form.useWatch('region', form)
  const startDate = Form.useWatch('startDate', form)
  const disableStartDate = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf('day')
  }

  const disableEndDate = (current: dayjs.Dayjs) => {
    const today = dayjs().startOf('day')
    const selectedStartDate = startDate
      ? dayjs(startDate).startOf('day')
      : today

    return current && current < selectedStartDate
  }

  const [
    updateManagedTicker,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      reset: updateReset,
    },
  ] = useUpdateManagedTickerMutation()

  const onFinish: FormProps<TickerManagementFormFieldTypes>['onFinish'] = (
    values
  ) => {
    createManagedTicker(values)
  }

  const onUpdate: FormProps<TickerManagementFormFieldTypes>['onFinish'] = (
    values
  ) => {
    updateManagedTicker({
      id: editTickerId!,
      ...values,
    })
  }
  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('ticker_management.form.success_message'),
    })
  }, [messageApi, t])

  const updateSuccess = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('ticker_management.form.update_success_message'),
    })
  }, [messageApi, t])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  useEffect(() => {
    if (isSuccess) {
      success()
      form.resetFields()
      onCancel()
      reset()
    }

    if (isUpdateSuccess) {
      updateSuccess()
      onCancel()
      updateReset()
    }
  }, [
    isSuccess,
    form,
    onCancel,
    reset,
    success,
    isUpdateSuccess,
    updateSuccess,
    updateReset,
  ])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }

    if (isUpdateError) {
      const description = getErrorDescription(updateError)
      errorNotification(description)
      updateReset()
    }
  }, [
    isError,
    error,
    reset,
    errorNotification,
    isUpdateError,
    updateError,
    updateReset,
  ])

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form
        form={form}
        layout={'vertical'}
        onFinish={mode === 'create' ? onFinish : onUpdate}
      >
        <Form.Item
          label={t('ticker_management.form.bbg_composite_key')}
          name={'bbgCompositeKey'}
          rules={[
            {
              required: true,
              message: t(
                'ticker_management.form.please_enter_bbg_composite_key'
              ),
            },
          ]}
          extra={
            <IdeyaTypography.Text type={'secondary'}>
              {t('ticker_management.form.capital_only')}
            </IdeyaTypography.Text>
          }
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={'region'}
          label={t('ticker_management.form.region')}
          rules={[
            {
              required: true,
              message: t('ticker_management.form.please_select_region'),
            },
          ]}
        >
          <Select
            placeholder={t('ticker_management.form.select_region')}
            options={regionOptions}
          />
        </Form.Item>
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginBase),
            convertPxToNumber(themeStyle.marginBase),
          ]}
        >
          <Col span={12}>
            <Form.Item
              label={t('ticker_management.form.can_long')}
              name={'canLong'}
              rules={[
                {
                  required: true,
                  message: t('ticker_management.form.please_select_option'),
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>
                  <IdeyaTypography.Text>{t('common.yes')}</IdeyaTypography.Text>
                </Radio>
                <Radio value={false}>
                  <IdeyaTypography.Text>{t('common.no')}</IdeyaTypography.Text>
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label={t('ticker_management.form.start_date')}
              name={'startDate'}
              rules={[
                {
                  required: true,
                  message: t('ticker_management.form.please_select_date'),
                },
              ]}
            >
              <IdeyaDatePicker
                disabledDate={disableStartDate}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('ticker_management.form.can_short')}
              name={'canShort'}
              rules={[
                {
                  required: true,
                  message: t('ticker_management.form.please_select_option'),
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>
                  <IdeyaTypography.Text>{t('common.yes')}</IdeyaTypography.Text>
                </Radio>
                <Radio value={false}>
                  <IdeyaTypography.Text>{t('common.no')}</IdeyaTypography.Text>
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label={t('ticker_management.form.end_date')}
              name={'endDate'}
            >
              <IdeyaDatePicker
                disabledDate={disableEndDate}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          {region === 'Greater China' && (
            <Col span={24}>
              <Form.Item
                label={t('ticker_management.form.is_adr')}
                name={'isAdr'}
                initialValue={false}
              >
                <Switch size={'small'} />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: themeStyle.marginXS,
            marginTop: themeStyle.marginXXL,
          }}
        >
          <IdeyaButton ghost onClick={handleCancel}>
            {t('common.cancel')}
          </IdeyaButton>
          <IdeyaButton
            loading={isLoading || isUpdateLoading}
            htmlType={'submit'}
          >
            {t('common.ok')}
          </IdeyaButton>
        </Row>
      </Form>
    </>
  )
}
